import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { UPDATE_CONVERSATION } from "../../services/graphql/conversation-operations";
import type { Conversation } from "../../generated/graphql";

interface ConversationCardProps {
  conversation: Conversation;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onRefetch: () => void;
}

export function ConversationCard({
  conversation,
  onArchive,
  onUnarchive,
  onRefetch,
}: ConversationCardProps) {
  const [showQuickEdit, setShowQuickEdit] = useState(false);
  const [quickEditLoading, setQuickEditLoading] = useState(false);

  // Helper function to determine if AI chat is enabled
  const isAIChatEnabled = () => {
    if (!conversation.aiConfig) return false;
    // If aiChatEnabled is explicitly set, use that value
    // If it's undefined but there's a content source or calendly integration, assume it should be enabled
    return conversation.aiConfig.aiChatEnabled !== undefined
      ? conversation.aiConfig.aiChatEnabled
      : !!(conversation.aiConfig.contentSource || conversation.aiConfig.calendlyIntegration?.enabled);
  };

  const [updateConversation] = useMutation(UPDATE_CONVERSATION, {
    onCompleted: () => {
      onRefetch();
      setShowQuickEdit(false);
    },
    onError: (error) => {
      console.error("Error updating conversation:", error);
      alert("Failed to update conversation. Please try again.");
    },
  });

  const handleQuickToggleAI = async () => {
    setQuickEditLoading(true);
    try {
      const currentlyEnabled = isAIChatEnabled();
      await updateConversation({
        variables: {
          input: {
            id: conversation.id,
            aiConfig: {
              aiChatEnabled: !currentlyEnabled,
              contentSource: conversation.aiConfig?.contentSource,
              calendlyIntegration: conversation.aiConfig?.calendlyIntegration,
              aiSettings: {
                model: "nova-lite",
                temperature: 0.7,
                maxTokens: 1000,
              },
            },
          },
        },
      });
    } catch (error) {
      console.error("Error toggling AI chat:", error);
    } finally {
      setQuickEditLoading(false);
    }
  };

  const getAIStatusColor = () => {
    if (!isAIChatEnabled()) return "gray";

    const contentStatus = conversation.aiConfig.contentSource?.analysisStatus;
    if (contentStatus === "COMPLETED") return "green";
    if (contentStatus === "PENDING") return "yellow";
    if (contentStatus === "FAILED") return "red";
    return "blue";
  };

  const getAIStatusText = () => {
    if (!isAIChatEnabled()) return "AI Disabled";

    const contentStatus = conversation.aiConfig.contentSource?.analysisStatus;
    if (contentStatus === "COMPLETED") return "AI Ready";
    if (contentStatus === "PENDING") return "Analyzing Content";
    if (contentStatus === "FAILED") return "Analysis Failed";
    return "AI Enabled";
  };

  const getCalendlyStatusColor = () => {
    if (!conversation.aiConfig?.calendlyIntegration?.enabled) return "gray";

    const status = conversation.aiConfig.calendlyIntegration.connectionStatus;
    if (status === "CONNECTED") return "green";
    if (status === "FAILED") return "red";
    return "yellow";
  };

  const getCalendlyStatusText = () => {
    if (!conversation.aiConfig?.calendlyIntegration?.enabled)
      return "Calendly Disabled";

    const status = conversation.aiConfig.calendlyIntegration.connectionStatus;
    if (status === "CONNECTED") return "Calendly Connected";
    if (status === "FAILED") return "Calendly Failed";
    return "Calendly Not Configured";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="p-4">
        <div className="flex items-start justify-between">
          {/* Main Content */}
          <Link
            to={`/conversations/create?edit=${conversation.id}`}
            className="flex items-start space-x-4 flex-1 min-w-0"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-blue-600 font-semibold">
                {conversation.name.substring(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">
                {conversation.name}
              </h3>
              {conversation.description && (
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                  {conversation.description}
                </p>
              )}
              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                <span>{conversation.assets?.length || 0} assets</span>
                <span>•</span>
                <span>
                  {conversation.autoResponder
                    ? conversation.autoResponder.name
                    : "No auto-responder"}
                </span>
                <span>•</span>
                <span>Updated {formatDate(conversation.updatedAt)}</span>
              </div>
            </div>
          </Link>

          {/* Status and Actions */}
          <div className="flex items-start space-x-3 flex-shrink-0 ml-4">
            {/* AI Chat Status */}
            {isAIChatEnabled() && (
              <div className="flex flex-col space-y-1">
                <div
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                    getAIStatusColor() === "green"
                      ? "bg-green-100 text-green-800"
                      : getAIStatusColor() === "yellow"
                      ? "bg-yellow-100 text-yellow-800"
                      : getAIStatusColor() === "red"
                      ? "bg-red-100 text-red-800"
                      : getAIStatusColor() === "blue"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  <svg
                    className="w-3 h-3 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  {getAIStatusText()}
                </div>

                {/* Calendly Status */}
                {conversation.aiConfig.calendlyIntegration?.enabled && (
                  <div
                    className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                      getCalendlyStatusColor() === "green"
                        ? "bg-green-100 text-green-800"
                        : getCalendlyStatusColor() === "red"
                        ? "bg-red-100 text-red-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V7a2 2 0 012-2h4a2 2 0 012 2v0M8 7v8a2 2 0 002 2h4a2 2 0 002-2V7M8 7H6a2 2 0 00-2 2v8a2 2 0 002 2h2m0 0h8m-8 0V9a2 2 0 012-2h4a2 2 0 012 2v8a2 2 0 01-2 2H10z"
                      />
                    </svg>
                    {getCalendlyStatusText()}
                  </div>
                )}
              </div>
            )}

            {/* Conversation Status */}
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                conversation.status === "ACTIVE"
                  ? "bg-green-100 text-green-800"
                  : conversation.status === "DRAFT"
                  ? "bg-yellow-100 text-yellow-800"
                  : conversation.status === "ARCHIVED"
                  ? "bg-gray-100 text-gray-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {conversation.status}
            </span>

            {/* Quick Actions */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowQuickEdit(!showQuickEdit);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                data-testid={`quick-actions-${conversation.id}`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                  />
                </svg>
              </button>

              {/* Quick Edit Dropdown */}
              {showQuickEdit && (
                <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="py-1">
                    <Link
                      to={`/conversations/create?edit=${conversation.id}`}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowQuickEdit(false)}
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit Conversation
                    </Link>

                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleQuickToggleAI();
                      }}
                      disabled={quickEditLoading}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      data-testid={`toggle-ai-${conversation.id}`}
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      {quickEditLoading
                        ? "Updating..."
                        : isAIChatEnabled()
                        ? "Disable AI Chat"
                        : "Enable AI Chat"}
                    </button>

                    {isAIChatEnabled() && (
                      <Link
                        to={`/analytics/ai?conversationId=${conversation.id}`}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowQuickEdit(false)}
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                        View AI Analytics
                      </Link>
                    )}

                    <div className="border-t border-gray-100 my-1"></div>

                    {conversation.status === "ARCHIVED" ? (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onUnarchive(conversation.id);
                          setShowQuickEdit(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                          />
                        </svg>
                        Unarchive
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onArchive(conversation.id);
                          setShowQuickEdit(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 8l4 4m0 0l4-4m-4 4V3"
                          />
                        </svg>
                        Archive
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Analysis Details */}
        {isAIChatEnabled() &&
          conversation.aiConfig?.contentSource && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center space-x-4">
                  <span>
                    Content:{" "}
                    {conversation.aiConfig.contentSource.type ===
                    "DESTINATION_URL"
                      ? "URL"
                      : "File"}
                  </span>
                  <span>•</span>
                  <span>
                    Status:{" "}
                    {conversation.aiConfig.contentSource.analysisStatus ||
                      "Unknown"}
                  </span>
                </div>

                {conversation.aiConfig.contentSource.analysisStatus ===
                  "FAILED" && (
                  <button
                    className="text-red-600 hover:text-red-700 font-medium"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // TODO: Implement retry analysis
                      alert(
                        "Retry analysis functionality would be implemented here"
                      );
                    }}
                  >
                    Retry Analysis
                  </button>
                )}
              </div>
            </div>
          )}
      </div>

      {/* Click outside to close dropdown */}
      {showQuickEdit && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowQuickEdit(false)}
        />
      )}
    </div>
  );
}
